import Head from "next/head";
import { useContext } from "react";
import { i18n } from "next-i18next";

import { TabIdentityContext } from "components/Navigation/TabIdentityContext";
import { composeTitle, iconLinks } from "utilities/tabIdentity";

// The templates live in common.json (tab_title.*), which nearly every page loads. Pages
// without translations at all (sign-in, reset, invitation…) have no i18n instance yet, and
// the pseudo-"keys" locale is a real locale — so ask the instance whether the key exists and
// fall back to the English default otherwise, instead of rendering a raw key.
function translateTemplate(key, vars) {
    try {
        if (i18n && typeof i18n.exists === "function" && i18n.exists(key)) return i18n.t(key, vars);
    } catch (e) {
        /* fall through to the default template */
    }
    return null;
}

/**
 * The one place a portal page sets its browser-tab title and favicon (SPEC-009).
 *
 *   <TabIdentity product="d6" content={t("sidebar:Plan")} port={portName} />
 *
 * - `product`: "omci" (Home, Administration, shared pages) | "d6" | "so" — from the route, never
 *   from entitlements or history (§3.3).
 * - `content`: the translated page content, already sentence-cased in the locale files.
 * - `port`: configured port display name for port-scoped pages; omitted when it is already in
 *   the content.
 * The environment prefix comes from TabIdentityContext (runtime OMC_ENV_TAG).
 *
 * next/head keeps the LAST <title> and, thanks to the fixed keys, the last icon links, so a
 * screen inside a page (the Monitor's vessel detail) can render its own <TabIdentity> while it
 * is open and the page's identity returns when it unmounts.
 */
export default function TabIdentity({ product = "omci", content, port = null, svg = false }) {
    const { envTag } = useContext(TabIdentityContext) || {};
    const title = composeTitle({ content, port, envTag, translate: translateTemplate });
    const icons = iconLinks(product);
    return (
        <Head>
            <title key="tab-title">{title}</title>
            <link key="tab-icon-32" rel="icon" type="image/png" sizes="32x32" href={icons.png32} />
            <link key="tab-icon-16" rel="icon" type="image/png" sizes="16x16" href={icons.png16} />
            {svg && <link key="tab-icon-svg" rel="icon" type="image/svg+xml" sizes="any" href={icons.svg} />}
        </Head>
    );
}
